import PropTypes from "prop-types";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Icon, ListSubheader, MenuItem, Select } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import TypographyV1 from "components/Atoms/Typography/TypographyV1";
import BoxV1 from "components/Atoms/Box/BoxV1";

function SelectWithSearch({ options, value, onChange, label, placeholder, minWidth, disabled }) {
  const { t } = useTranslation(["common"]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const paperRef = useRef(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const filtered = options.filter((opt) => opt.label.toLowerCase().includes(search.toLowerCase()));
  const hasResults = filtered.length > 0;

  const dropdownBg = isDark ? "#1a2332" : "background.paper";

  const triggerBg = isDark ? "rgba(255,255,255,0.11)" : "#f4f6f8";
  const triggerHoverBg = isDark ? "rgba(255,255,255,0.14)" : "#eceef1";
  const borderColor = isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.15)";
  const borderHover = isDark ? "rgba(255,255,255,0.32)" : "rgba(0,0,0,0.28)";
  const borderFocus = isDark ? "rgba(255,255,255,0.38)" : "rgba(0,0,0,0.32)";
  const textColor = isDark ? theme.palette.text.primary : "inherit";
  const placeholderColor = isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.38)";
  const iconColor = isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)";
  const searchIconColor = isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.4)";
  const searchPlaceholderColor = isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.38)";

  return (
    <Box sx={{ minWidth }}>
      {label && (
        <BoxV1 mb={1} ml={0.5} lineHeight={0} display="inline-block">
          <TypographyV1 component="label" variant="caption" fontWeight="bold" textTransform="none">
            {label}
          </TypographyV1>
        </BoxV1>
      )}
      <Box
        onClick={() => {
          if (!open && !disabled) {
            setOpen(true);
          }
        }}
        sx={{
          position: "relative",
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        <Select
          value={value}
          onChange={onChange}
          displayEmpty
          size="small"
          fullWidth
          variant="outlined"
          disabled={disabled}
          open={disabled ? false : open}
          onOpen={() => {
            if (!disabled) {
              setOpen(true);
              setTimeout(() => {
                if (paperRef.current) paperRef.current.scrollTop = 0;
              }, 0);
            }
          }}
          onClose={() => {
            setOpen(false);
            setSearch("");
          }}
          IconComponent={(props) => (
            <Icon {...props} sx={{ pointerEvents: "none" }}>
              arrow_drop_down
            </Icon>
          )}
          MenuProps={{
            autoFocus: false,
            MenuListProps: { autoFocusItem: false },
            disablePortal: false,
            PaperProps: {
              ref: paperRef,
              sx: {
                maxHeight: 320,
                borderRadius: 2,
                mt: 0.5,
                minWidth: 260,
                bgcolor: dropdownBg,
                ...(isDark && {
                  border: "1px solid rgba(255,255,255,0.1)",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
                }),
                "& .MuiList-root": {
                  bgcolor: "transparent",
                },
              },
            },
            sx: {
              zIndex: 1301,
            },
          }}
          sx={{
            fontSize: "0.85rem",
            backgroundColor: `${triggerBg} !important`,
            borderRadius: "8px",
            color: textColor,
            transition: "background-color 0.15s",
            cursor: "pointer",
            pointerEvents: "auto",
            "& .MuiSelect-select": {
              paddingTop: "9px",
              paddingBottom: "9px",
              paddingLeft: "12px",
              paddingRight: "36px !important",
              minHeight: "auto",
              height: "100%",
              display: "block",
              background: "transparent !important",
              backgroundColor: "transparent !important",
              cursor: "pointer",
              pointerEvents: "auto",
              "& span": {
                padding: 0,
                margin: 0,
                background: "transparent !important",
                backgroundColor: "transparent !important",
                pointerEvents: "none",
                display: "inline",
              },
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: `${borderColor} !important`,
              pointerEvents: "none",
            },
            "& .MuiSelect-icon": {
              color: iconColor,
              right: "10px",
              fontSize: "1.4rem",
              transition: "transform 0.2s",
              pointerEvents: "none",
            },
            "& .MuiSelect-iconOpen": {
              transform: "rotate(180deg)",
            },
            "&:hover": {
              backgroundColor: `${triggerHoverBg} !important`,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: `${borderHover} !important`,
              },
            },
            "&.Mui-focused": {
              backgroundColor: `${triggerHoverBg} !important`,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: `${borderFocus} !important`,
              },
              boxShadow: "none !important",
            },
          }}
          renderValue={(selected) => {
            if (selected === null || selected === undefined || selected === "") {
              return (
                <span
                  style={{
                    color: placeholderColor,
                    fontStyle: "italic",
                    background: "transparent",
                    backgroundColor: "transparent",
                    padding: 0,
                    margin: 0,
                    pointerEvents: "none",
                  }}
                >
                  {placeholder ?? t("all")}
                </span>
              );
            }
            return options.find((o) => o.value === selected)?.label ?? String(selected);
          }}
        >
          <ListSubheader
            sx={{
              px: 1.5,
              pt: 1.25,
              pb: 0.75,
              lineHeight: "normal",
              bgcolor: dropdownBg,
              background: dropdownBg,
              position: "static !important",
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                px: 1.25,
                py: 0.75,
                gap: 0.75,
                backgroundColor: "transparent",
              }}
            >
              <Icon
                sx={{
                  fontSize: "1rem !important",
                  color: searchIconColor,
                  flexShrink: 0,
                  lineHeight: 1,
                }}
              >
                search
              </Icon>
              <Box
                component="input"
                type="text"
                placeholder={t("search")}
                value={search}
                autoFocus
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setOpen(false);
                    setSearch("");
                  } else if (e.key !== "ArrowDown" && e.key !== "ArrowUp") {
                    e.stopPropagation();
                  }
                }}
                sx={{
                  border: "none",
                  outline: "none",
                  background: "transparent !important",
                  backgroundColor: "transparent !important",
                  color: textColor,
                  fontSize: "0.85rem",
                  width: "100%",
                  lineHeight: 1.5,
                  "::placeholder": { color: searchPlaceholderColor },
                  "&:-webkit-autofill": {
                    WebkitBoxShadow: "0 0 0 100px transparent inset !important",
                    WebkitTextFillColor: textColor,
                  },
                }}
              />
            </Box>
          </ListSubheader>
          {hasResults ? (
            filtered.map((opt) => (
              <MenuItem
                key={opt.value}
                value={opt.value}
                sx={{ fontSize: "0.875rem", py: 1, px: 2 }}
              >
                {opt.label}
              </MenuItem>
            ))
          ) : (
            <MenuItem
              disabled
              sx={{
                fontSize: "0.82rem",
                py: 1,
                px: 2,
                color: "text.secondary",
                fontStyle: "italic",
              }}
            >
              {t("no_results")}
            </MenuItem>
          )}
        </Select>
      </Box>
    </Box>
  );
}

SelectWithSearch.defaultProps = {
  placeholder: undefined,
  minWidth: 200,
  disabled: false,
};

SelectWithSearch.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  disabled: PropTypes.bool,
};

export default SelectWithSearch;
